import PasswordRequirementsV9 from './PasswordRequirementsV9';
import { isValidNewPasswordV9 } from '../helpers/passwordPolicyV9';
import { isValidUsernameV108 } from '../helpers/usernamePolicyV108';
import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Button,
  Form,
  Grid,
  Header,
  Image,
  Message,
  Card,
  Divider,
} from 'semantic-ui-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API, showError, showInfo, showSuccess } from '../helpers';
import { useSystemBrandingV9 } from '../context/SystemBrandingV9';
import { StatusContext } from '../context/Status';
import Turnstile from 'react-turnstile';
import RegistrationPuzzleV108 from './RegistrationPuzzleV108';

const RegisterForm = () => {
  const { t, i18n } = useTranslation();
  const statusContext = useContext(StatusContext);
  const liveStatus = Array.isArray(statusContext)
    ? statusContext[0]?.status
    : undefined;
  const text = (zh, en) => (i18n?.language?.startsWith('zh') ? zh : en);
  const [inputs, setInputs] = useState(() => ({
    username: '',
    password: '',
    password2: '',
    email: '',
    verification_code: '',
    invitation_code:
      new URLSearchParams(window.location.hash.slice(1))
        .get('invite')
        ?.trim() || '',
  }));
  const [organizationName, setOrganizationName] = useState('');
  const creationInvitation = inputs.invitation_code.trim().startsWith('orgc_');
  const { username, password, password2 } = inputs;
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [turnstileEnabled, setTurnstileEnabled] = useState(false);
  const [localCaptcha, setLocalCaptcha] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaRevision, setCaptchaRevision] = useState(0);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileRevision, setTurnstileRevision] = useState(0);
  const [turnstileError, setTurnstileError] = useState(false);
  const turnstileWidget = useRef(null);
  const [loading, setLoading] = useState(false);
  const [registerEnabled, setRegisterEnabled] = useState(true);
  const requestPending = useRef(false);
  const mounted = useRef(true);
  const [disableButton, setDisableButton] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const { logo, onLogoError } = useSystemBrandingV9();
  let affCode = new URLSearchParams(window.location.search).get('aff');
  if (affCode) {
    localStorage.setItem('aff', affCode);
  }

  useEffect(() => {
    mounted.current = true;
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    if (fragment.has('invite')) {
      fragment.delete('invite');
      const remaining = fragment.toString();
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${window.location.search}${
          remaining ? `#${remaining}` : ''
        }`
      );
    }
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    try {
      const status =
        liveStatus || JSON.parse(localStorage.getItem('status') || '{}');
      setRegisterEnabled(
        status?.register_enabled !== false &&
          status?.password_register_enabled !== false
      );
      setShowEmailVerification(Boolean(status?.email_verification));
      setLocalCaptcha(status?.registration_captcha_provider === 'local');
      setTurnstileEnabled(
        status?.registration_captcha_provider === 'turnstile' ||
          (!status?.registration_captcha_provider &&
            Boolean(status?.turnstile_check))
      );
      setTurnstileSiteKey(status?.turnstile_site_key || '');
    } catch (_) {
      /* Keep the existing defaults when cached status is malformed. */
    }
  }, [liveStatus]);

  useEffect(() => {
    setTurnstileToken('');
    setTurnstileError(false);
    turnstileWidget.current = null;
  }, [turnstileEnabled, turnstileSiteKey]);

  useEffect(() => {
    let countdownInterval = null;
    if (disableButton && countdown > 0) {
      countdownInterval = setInterval(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0) {
      setDisableButton(false);
      setCountdown(30);
    }
    return () => clearInterval(countdownInterval);
  }, [disableButton, countdown]);

  let navigate = useNavigate();

  function handleChange(e) {
    const { name, value } = e.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  }

  function resetTurnstile() {
    setCaptchaToken('');
    setCaptchaRevision((revision) => revision + 1);
    setTurnstileToken('');
    setTurnstileError(false);
    if (turnstileWidget.current) {
      turnstileWidget.current.reset();
    } else {
      setTurnstileRevision((revision) => revision + 1);
    }
  }

  function failTurnstile() {
    setTurnstileToken('');
    setTurnstileError(true);
  }

  async function handleSubmit(e) {
    e?.preventDefault();
    if (requestPending.current || loading || !registerEnabled) return;
    if (!inputs.invitation_code.trim()) {
      showInfo(text('请输入邀请码', 'Enter an invitation code'));
      return;
    }
    if (
      creationInvitation &&
      Array.from(organizationName.trim()).length > 191
    ) {
      showInfo(
        text(
          '组织名称最多 191 字',
          'Organization name must be at most 191 characters'
        )
      );
      return;
    }
    if (!isValidUsernameV108(username)) {
      showInfo(t('organization.validation.username_length'));
      return;
    }
    if (!isValidNewPasswordV9(password)) {
      showInfo(t('password_v9.error'));
      return;
    }
    if (password !== password2) {
      showInfo(t('messages.error.password_mismatch'));
      return;
    }
    if (username && password) {
      if (
        (turnstileEnabled && turnstileToken === '') ||
        (localCaptcha && !captchaToken)
      ) {
        showInfo(t('messages.error.turnstile_wait'));
        return;
      }
      requestPending.current = true;
      setLoading(true);
      try {
        const res = await API.post(
          '/api/user/register',
          {
            ...inputs,
            invitation_code: inputs.invitation_code.trim(),
            ...(creationInvitation
              ? { organization_name: organizationName.trim() }
              : {}),
            aff_code: affCode || localStorage.getItem('aff'),
          },
          {
            headers: {
              'X-Turnstile-Token': turnstileToken,
              ...(localCaptcha
                ? { 'X-Registration-Captcha': captchaToken }
                : {}),
            },
          }
        );
        if (!mounted.current) return;
        if (!res?.data?.success)
          throw new Error(
            res?.data?.message ||
              text('注册失败，请重试', 'Registration failed. Try again.')
          );
        navigate('/login');
        showSuccess(t('messages.success.register'));
      } catch (error) {
        if (mounted.current)
          showError(
            error.response?.data?.message ||
              error.message ||
              text('注册失败，请重试', 'Registration failed. Try again.')
          );
      } finally {
        requestPending.current = false;
        if (mounted.current) {
          setLoading(false);
          if (turnstileEnabled || localCaptcha) resetTurnstile();
        }
      }
    }
  }

  const sendVerificationCode = async () => {
    if (
      inputs.email === '' ||
      requestPending.current ||
      disableButton ||
      !registerEnabled
    )
      return;
    if (
      (turnstileEnabled && turnstileToken === '') ||
      (localCaptcha && !captchaToken)
    ) {
      showInfo(t('messages.error.turnstile_wait'));
      return;
    }
    requestPending.current = true;
    setDisableButton(true);
    setLoading(true);
    try {
      const res = await API.get(
        `/api/verification?email=${encodeURIComponent(
          inputs.email
        )}&turnstile=${encodeURIComponent(turnstileToken)}`,
        {
          headers: {
            'X-Registration-Captcha': captchaToken,
            'X-Turnstile-Token': turnstileToken,
          },
        }
      );
      if (!mounted.current) return;
      if (!res?.data?.success)
        throw new Error(
          res?.data?.message ||
            text(
              '验证码发送失败，请重试',
              'Verification email failed. Try again.'
            )
        );
      showSuccess(t('messages.success.verification_code'));
    } catch (error) {
      if (mounted.current) {
        showError(
          error.response?.data?.message ||
            error.message ||
            text(
              '验证码发送失败，请重试',
              'Verification email failed. Try again.'
            )
        );
        setDisableButton(false);
        setCountdown(30);
      }
    } finally {
      requestPending.current = false;
      if (mounted.current) {
        setLoading(false);
        if (turnstileEnabled || localCaptcha) resetTurnstile();
      }
    }
  };

  return (
    <Grid textAlign='center' style={{ marginTop: '48px' }}>
      <Grid.Column style={{ maxWidth: 450 }}>
        <Card
          fluid
          className='chart-card'
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}
        >
          <Card.Content>
            <Card.Header>
              <Header
                as='h2'
                textAlign='center'
                style={{ marginBottom: '1.5em' }}
              >
                <Image
                  src={logo}
                  onError={onLogoError}
                  style={{ marginBottom: '10px' }}
                />
                <Header.Content>
                  {creationInvitation
                    ? text('创建新组织', 'Create a new organization')
                    : t('auth.register.title')}
                </Header.Content>
              </Header>
            </Card.Header>
            {!registerEnabled ? (
              <Message
                warning
                role='status'
                content={text('注册已关闭', 'Registration is closed')}
              />
            ) : (
              <Form size='large' onSubmit={handleSubmit}>
                <Form.Input
                  disabled={loading}
                  id='registration-invitation-v108'
                  fluid
                  required
                  icon='ticket'
                  iconPosition='left'
                  label={text('邀请码', 'Invitation code')}
                  placeholder={text('邀请码', 'Invitation code')}
                  name='invitation_code'
                  value={inputs.invitation_code}
                  onChange={handleChange}
                  autoComplete='off'
                  spellCheck={false}
                  style={{ marginBottom: '1em' }}
                />
                {creationInvitation && (
                  <Form.Input
                    id='registration-organization-v109'
                    fluid
                    icon='building outline'
                    iconPosition='left'
                    label={text(
                      '组织名称（可选）',
                      'Organization name (optional)'
                    )}
                    placeholder={text('默认使用用户名', 'Defaults to username')}
                    name='organization_name'
                    value={organizationName}
                    disabled={loading}
                    onChange={(_, { value }) => setOrganizationName(value)}
                    autoComplete='organization'
                    style={{ marginBottom: '1em' }}
                  />
                )}
                <Form.Input
                  fluid
                  icon='user'
                  iconPosition='left'
                  placeholder={t('auth.register.username')}
                  onChange={handleChange}
                  name='username'
                  autoComplete='username'
                  error={
                    Boolean(username) &&
                    !isValidUsernameV108(username) && {
                      content: t('organization.validation.username_length'),
                    }
                  }
                  style={{ marginBottom: '1em' }}
                />
                <Form.Input
                  fluid
                  icon='lock'
                  iconPosition='left'
                  placeholder={t('auth.register.password')}
                  onChange={handleChange}
                  name='password'
                  autoComplete='new-password'
                  type='password'
                  style={{ marginBottom: '1em' }}
                />
                <PasswordRequirementsV9 password={password} />
                <Form.Input
                  fluid
                  icon='lock'
                  iconPosition='left'
                  placeholder={t('auth.register.confirm_password')}
                  onChange={handleChange}
                  name='password2'
                  type='password'
                  style={{ marginBottom: '1em' }}
                />

                {showEmailVerification && (
                  <>
                    <Form.Input
                      fluid
                      icon='mail'
                      iconPosition='left'
                      placeholder={t('auth.register.email')}
                      onChange={handleChange}
                      name='email'
                      type='email'
                      action={
                        <Button
                          type='button'
                          onClick={sendVerificationCode}
                          disabled={
                            loading ||
                            disableButton ||
                            (localCaptcha && !captchaToken)
                          }
                        >
                          {disableButton
                            ? t('auth.register.get_code_retry', { countdown })
                            : t('auth.register.get_code')}
                        </Button>
                      }
                      style={{ marginBottom: '1em' }}
                    />
                    <Form.Input
                      fluid
                      icon='lock'
                      iconPosition='left'
                      placeholder={t('auth.register.verification_code')}
                      onChange={handleChange}
                      name='verification_code'
                      style={{ marginBottom: '1em' }}
                    />
                  </>
                )}

                {localCaptcha && (
                  <RegistrationPuzzleV108
                    onChange={setCaptchaToken}
                    revision={captchaRevision}
                  />
                )}
                {turnstileEnabled && (
                  <div
                    role='group'
                    aria-label={text('人机验证', 'Human verification')}
                    style={{
                      marginBottom: '1em',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    {turnstileSiteKey ? (
                      <Turnstile
                        key={`${turnstileSiteKey}-${turnstileRevision}`}
                        sitekey={turnstileSiteKey}
                        action='registration'
                        size='compact'
                        theme='auto'
                        onLoad={(_, widget) => {
                          turnstileWidget.current = widget;
                        }}
                        onVerify={(token) => {
                          setTurnstileToken(token);
                          setTurnstileError(false);
                        }}
                        onExpire={() => setTurnstileToken('')}
                        onError={failTurnstile}
                        onTimeout={failTurnstile}
                        onUnsupported={failTurnstile}
                      />
                    ) : (
                      <Message
                        warning
                        visible
                        role='alert'
                        content={text(
                          '人机验证配置未完成，请联系管理员',
                          'Human verification is not configured. Contact an administrator.'
                        )}
                      />
                    )}
                    {turnstileError && (
                      <Message warning visible role='alert'>
                        {text(
                          '人机验证未完成，请重试或刷新页面',
                          'Human verification could not complete. Retry or reload the page.'
                        )}
                        <Button
                          type='button'
                          basic
                          icon='refresh'
                          aria-label={text(
                            '重试人机验证',
                            'Retry verification'
                          )}
                          title={text('重试人机验证', 'Retry verification')}
                          onClick={resetTurnstile}
                        />
                      </Message>
                    )}
                  </div>
                )}

                <Button
                  fluid
                  size='large'
                  type='submit'
                  disabled={
                    loading ||
                    !inputs.invitation_code.trim() ||
                    !isValidUsernameV108(username) ||
                    !isValidNewPasswordV9(password) ||
                    password !== password2 ||
                    (turnstileEnabled && !turnstileToken) ||
                    (localCaptcha && !captchaToken) ||
                    (creationInvitation &&
                      Array.from(organizationName.trim()).length > 191)
                  }
                  style={{
                    background: '#2F73FF', // 使用更现代的蓝色
                    color: 'white',
                    marginBottom: '1.5em',
                  }}
                  loading={loading}
                >
                  {t('auth.register.button')}
                </Button>
              </Form>
            )}

            <Divider />
            <Message style={{ background: 'transparent', boxShadow: 'none' }}>
              <div
                style={{
                  textAlign: 'center',
                  fontSize: '0.9em',
                  color: '#666',
                }}
              >
                {t('auth.register.has_account')}
                <Link
                  to='/login'
                  style={{ color: '#2185d0', marginLeft: '2px' }}
                >
                  {t('auth.register.login')}
                </Link>
              </div>
            </Message>
          </Card.Content>
        </Card>
      </Grid.Column>
    </Grid>
  );
};

export default RegisterForm;
